import React, { useCallback, useRef } from "react";

/**
 * Single sticker chip — memoised so toggling one number does not re-render
 * the other 649 chips.
 *
 *   state ∈ "duplicate" | "missing" | "neither"
 *
 * Phase 22a — Duplicate chips are draggable (HTML5 for desktop pointers,
 * synthetic touch tracking for mobile). Payload MIME:
 *   application/x-figumatch-listing
 * Payload body: {"number": <int>, "grade": "<M|NM|EX|G|F|HW|null>"}
 *
 * Grade comes from Phase 21's `sticker_grades` map, threaded down via prop.
 * When the user releases the touch/drag over the MarketPage drop-zone
 * (identified by `[data-testid="market-drop-zone"]`), a custom event
 * `figumatch:listing-touch-drop` fires on `document` with `{detail: {number,
 * grade}}` — the MarketPage listens for it.
 */
const DRAG_MIME = "application/x-figumatch-listing";
const DROP_ZONE_SELECTOR = '[data-testid="market-drop-zone"]';

const StickerChip = React.memo(function StickerChip({
  number,
  state,
  grade,
  onClick,
}) {
  const ghostRef = useRef(null);
  const touchStartRef = useRef(null);

  let cls;
  if (state === "duplicate") {
    cls =
      "bg-[#E5B83A] text-black border-[#E5B83A] hover:bg-[#F0C95C] " +
      "shadow-[0_0_0_1px_rgba(229,184,58,0.5)]";
  } else if (state === "missing") {
    cls =
      "bg-transparent text-[#E5B83A] border-[#E5B83A] hover:bg-[#E5B83A]/10";
  } else {
    cls =
      "bg-[#1a1a1a] text-[#555] border-transparent hover:bg-[#222] hover:text-[#888]";
  }

  const isDuplicate = state === "duplicate";
  const payload = { number, grade: grade || null };

  // ── HTML5 drag (desktop) ─────────────────────────────────────────
  const onDragStart = useCallback(
    (e) => {
      if (!isDuplicate) return;
      try {
        const body = JSON.stringify(payload);
        e.dataTransfer.setData(DRAG_MIME, body);
        e.dataTransfer.setData("text/plain", body);
        e.dataTransfer.effectAllowed = "copy";
      } catch (_) {
        /* ignore */
      }
    },
    [isDuplicate, payload]
  );

  // ── Touch drag (mobile) — synthetic ghost + elementFromPoint ─────
  const cleanupGhost = () => {
    if (ghostRef.current && ghostRef.current.parentNode) {
      ghostRef.current.parentNode.removeChild(ghostRef.current);
    }
    ghostRef.current = null;
  };

  const onTouchStart = useCallback(
    (e) => {
      if (!isDuplicate) return;
      if (!e.touches || e.touches.length !== 1) return;
      const t = e.touches[0];
      touchStartRef.current = { x: t.clientX, y: t.clientY, moved: false };
    },
    [isDuplicate]
  );

  const onTouchMove = useCallback(
    (e) => {
      if (!isDuplicate) return;
      const start = touchStartRef.current;
      if (!start || !e.touches || e.touches.length !== 1) return;
      const t = e.touches[0];
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      if (!start.moved && Math.hypot(dx, dy) < 8) return; // tap tolerance
      start.moved = true;
      // Create ghost on first meaningful move.
      if (!ghostRef.current) {
        const g = document.createElement("div");
        g.textContent = String(number);
        g.style.cssText =
          "position:fixed;z-index:9999;pointer-events:none;" +
          "background:#E5B83A;color:#000;font:600 12px/1 monospace;" +
          "border-radius:6px;padding:6px 10px;" +
          "box-shadow:0 8px 24px rgba(0,0,0,0.6);opacity:0.9;";
        document.body.appendChild(g);
        ghostRef.current = g;
      }
      ghostRef.current.style.left = `${t.clientX - 20}px`;
      ghostRef.current.style.top = `${t.clientY - 16}px`;
      // Prevent scrolling while dragging.
      if (e.cancelable) e.preventDefault();
    },
    [isDuplicate, number]
  );

  const onTouchEnd = useCallback(
    (e) => {
      const start = touchStartRef.current;
      touchStartRef.current = null;
      const ghost = ghostRef.current;
      cleanupGhost();
      if (!isDuplicate) return;
      if (!start || !start.moved) return; // treat as tap → let onClick fire
      // Consume the touchend so onClick doesn't also toggle the sticker.
      if (e.cancelable) e.preventDefault();
      const touch =
        (e.changedTouches && e.changedTouches[0]) || null;
      if (!touch || !ghost) return;
      // Temporarily hide the ghost was already done; now hit-test.
      const target = document.elementFromPoint(touch.clientX, touch.clientY);
      const zone =
        target && target.closest ? target.closest(DROP_ZONE_SELECTOR) : null;
      if (zone) {
        document.dispatchEvent(
          new CustomEvent("figumatch:listing-touch-drop", {
            detail: payload,
          })
        );
      }
    },
    [isDuplicate, payload]
  );

  return (
    <button
      type="button"
      id={`chip-${number}`}
      data-testid={`chip-${number}`}
      data-state={state}
      data-listable={isDuplicate ? "true" : "false"}
      draggable={isDuplicate}
      onDragStart={onDragStart}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClick={() => onClick(number)}
      className={
        // Phase 17a: `a11y-tap-exempt` keeps the 36px chip size in ASL mode
        // (the 44×44 tap-target min applies to buttons/links except this class).
        "a11y-tap-exempt h-9 w-full rounded-md border text-xs font-medium font-body " +
        "transition-colors active:scale-95 select-none " +
        (isDuplicate ? "cursor-grab " : "") +
        cls
      }
    >
      {number}
    </button>
  );
});

export default StickerChip;
